import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import loginSuccess from '../../context/actions/auth/loginSuccess';
import { GlobalContext, handleError } from '../../context/Provider';
import henceforthApi from '../../utils/henceforthApi';
import henceofrthEnums from '../../utils/henceofrthEnums';
import Notification from './Notification';
import profile_img from '../../assets/images/pages/defaultImage.jpg';
import {
  Globe,
  Bell,
  ChevronDown,
  Menu,
  User,
  KeyRound,
  LogOut
} from 'lucide-react';

interface TheHeaderProps {
  changeDiv?: (val: boolean) => void;
  onToggleSidebar?: () => void;
}

const TheHeader: React.FC<TheHeaderProps> = (props) => {
  const { authState, logOutNow, authDispatch, language_data, loading, setLoading } = useContext(GlobalContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [hideShow, setHideShow] = useState<boolean>(true);
  const [messages, setMessages] = useState({
    unread_notifications: [],
    read_notifications: [],
    unread_count: 0,
  });

  const onChangeLanguage = (value: string) => {
    loginSuccess({ lang: value })(authDispatch);
  };

  const toggleHandler = () => {
    if (props.onToggleSidebar) {
      props.onToggleSidebar();
    } else if (props.changeDiv) {
      const next = !hideShow;
      setHideShow(next);
      props.changeDiv(next);
    }
  };

  const logOut = async () => {
    Swal.fire({
      title: `${language_data?.[authState.lang]?.common_are_you_sure || 'Are you sure?'}`,
      text: `${language_data?.[authState.lang]?.common_are_sure_want_to || 'Are you sure you want to'} ${language_data?.[authState.lang]?.common_logout || 'log out'}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#1AA7DE',
      cancelButtonColor: '#ef4444',
      cancelButtonText: `${language_data?.[authState.lang]?.common_cancel || 'Cancel'}`,
      confirmButtonText: `${language_data?.[authState.lang]?.common_yes || 'Yes, Logout'}`,
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        logOutNow();
      }
    });
  };

  const getMessagesApi = async () => {
    try {
      const res = (await henceforthApi.Notification.getNotification(0, 10)).data;
      setMessages(res);
    } catch (err) {
      handleError(err, '', authDispatch);
    }
  };

  const markClearNotification = async (seletApi: string) => {
    if (loading) return;
    setLoading(true);
    try {
      if (seletApi) await henceforthApi.Notification.markAllAsRead({});
      else await henceforthApi.Notification.clearAllNotification({});
      await getMessagesApi();
    } catch (error) {
      handleError(error, '', authDispatch);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getMessagesApi();
  }, [location.pathname]);

  const avatarUrl = authState?.image
    ? authState.image.endsWith('.webp')
      ? `${henceforthApi.API_FILE_ROOT_ORIGINAL}${authState.image}`
      : `${henceforthApi.API_FILE_ROOT_MEDIUM}${authState.image}`
    : profile_img;

  return (
    <div className="w-full bg-white border-b border-slate-100 shadow-[0_1px_3px_0_rgba(0,0,0,0.02)] sticky top-0 z-40 transition-all">
      <div className="px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between gap-4">
        {/* Left Side: Mobile Toggle, Store Selector, Search Bar */}
        <div className="flex items-center gap-3 sm:gap-4 flex-1 max-w-2xl">
          {/* Mobile Collapse Toggle */}
          <button
            type="button"
            id="sidebar-toggle-btn"
            onClick={toggleHandler}
            title="Toggle Sidebar"
            aria-label="Toggle Sidebar"
            className="p-2.5 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-100/80 transition-all flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 active:scale-95 shadow-sm bg-white shrink-0 cursor-pointer"
          >
            <Menu className="w-5 h-5 text-slate-700" />
          </button>
        </div>

        {/* Right Side: Filters, Language, Notification Bell, Add Button, Avatar */}
        <div className="flex items-center gap-2 sm:gap-3">

          {/* Language Selector */}
          <div className="dropdown">
            <button
              className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl text-base font-semibold text-slate-700 hover:bg-slate-100/70 transition-all border border-transparent hover:border-slate-200/70"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <Globe className="w-4.5 h-4.5 text-slate-500" />
              <span className="uppercase">{authState.lang === 'ARABIC' ? 'AR' : 'EN'}</span>
              <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
            </button>
            <ul className="dropdown-menu py-2 rounded-2xl shadow-xl border border-slate-100 text-base min-w-[150px]">
              <li
                className="px-4 py-2 hover:bg-slate-50 cursor-pointer font-medium flex items-center justify-between"
                role="button"
                onClick={() => onChangeLanguage(henceofrthEnums.Language.ENGLISH)}
              >
                <span>English (EN)</span>
                {authState.lang !== 'ARABIC' && <span className="w-2 h-2 rounded-full bg-[#1AA7DE]"></span>}
              </li>
              <li
                className="px-4 py-2 hover:bg-slate-50 cursor-pointer font-medium flex items-center justify-between"
                role="button"
                onClick={() => onChangeLanguage(henceofrthEnums.Language.ARABIC)}
              >
                <span>Arabic (AR)</span>
                {authState.lang === 'ARABIC' && <span className="w-2 h-2 rounded-full bg-[#1AA7DE]"></span>}
              </li>
            </ul>
          </div>

          {/* Notification Bell */}
          <div className="dropdown">
            <button
              className="relative p-2.5 rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100/70 transition-all"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              onClick={getMessagesApi}
            >
              <Bell className="w-5 h-5 text-slate-600" />
              {messages.unread_count > 0 && (
                <span className="absolute top-1 right-1 min-w-[20px] h-5 px-1 bg-red-500 text-white rounded-full text-xs font-black flex items-center justify-center leading-none shadow-sm">
                  {messages.unread_count > 99 ? '99+' : messages.unread_count}
                </span>
              )}
            </button>
            <ul className="dropdown-menu dropdown-menu-end py-0 border-0 rounded-2xl shadow-2xl notification-dropdown overflow-hidden min-w-[340px]">
              <li className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/70">
                <span className="font-bold text-base text-slate-800">Notifications</span>
                <div className="flex gap-3 text-xs sm:text-sm">
                  <button
                    className="text-[#1AA7DE] hover:underline font-semibold"
                    onClick={() => markClearNotification('markDone')}
                  >
                    Mark read
                  </button>
                  <button
                    className="text-red-500 hover:underline font-semibold"
                    onClick={() => markClearNotification('')}
                  >
                    Clear
                  </button>
                </div>
              </li>
              <Notification
                NotificationHeading="New For You"
                checkLength={messages.unread_notifications.length}
                Array={messages.unread_notifications}
                Notifications={language_data?.[authState.lang]?.common_nothing_new_for_you || 'No new notifications'}
                getMessagesApi={getMessagesApi}
              />
              <Notification
                NotificationHeading="Previous notifications"
                checkLength={messages.read_notifications.length}
                Array={messages.read_notifications}
                Notifications=""
                getMessagesApi={getMessagesApi}
                readNotification={true}
                viewAll={messages.unread_notifications.length === 10 || messages.read_notifications.length === 10}
              />
            </ul>
          </div>

          {/* User Profile Avatar with dropdown */}
          <div className="dropdown">
            <button
              className="p-0 border-0 bg-transparent rounded-full focus:outline-none transition-all"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <div className="relative">
                <img
                  src={avatarUrl}
                  alt="Profile"
                  className="w-11 h-11 rounded-full object-cover border-2 border-slate-200/80 hover:border-[#1AA7DE] transition-all"
                />
              </div>
            </button>
            <ul className="dropdown-menu dropdown-menu-end py-1.5 rounded-2xl shadow-xl border border-slate-100 text-base min-w-[220px]">
              <li className="px-4 py-3 border-b border-slate-100 bg-slate-50/50">
                <p className="font-bold text-base text-slate-800 m-0 truncate capitalize">{authState.name || 'mayank'}</p>
                <p className="text-sm text-slate-500 m-0 truncate">{authState.email || 'mayank.henceforth@gmail.com'}</p>
              </li>
              <li className="pt-1">
                <Link className="dropdown-item px-4 py-2 flex items-center gap-2.5 text-slate-700 hover:bg-slate-50 font-medium" to="/profile">
                  <User className="w-4 h-4 text-slate-400" />
                  <span>{language_data?.[authState.lang]?.common_profile || 'Profile'}</span>
                </Link>
              </li>
              <li>
                <Link className="dropdown-item px-4 py-2 flex items-center gap-2.5 text-slate-700 hover:bg-slate-50 font-medium" to="/change-password">
                  <KeyRound className="w-4 h-4 text-slate-400" />
                  <span>{language_data?.[authState.lang]?.common_change_password || 'Change Password'}</span>
                </Link>
              </li>
              <li className="border-t border-slate-100 my-1"></li>
              <li className="pb-1">
                <button
                  type="button"
                  className="dropdown-item px-4 py-2 flex items-center gap-2.5 text-red-600 hover:bg-red-50 font-medium w-full text-left"
                  onClick={logOut}
                >
                  <LogOut className="w-4 h-4 text-red-500" />
                  <span>{language_data?.[authState.lang]?.common_logout || 'Log Out'}</span>
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TheHeader;